import cn from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  CHECKOUT_STEP_MAP,
  EDIT_BILLING_ADDRESS_STEP,
  NEW_BILLING_ADDRESS_STEP,
  NEW_PAYMENT_STEP,
  PAYMENT_STEP,
  REVIEW_STEP,
  SELECT_BILLING_ADDRESS_STEP
} from 'constants/checkoutFlow';

import { trackEvent } from 'helpers/analytics';
import {
  addNewAddress,
  clearAddressErrors,
  clearSelectedAddress,
  clearSuggestedAddresses,
  deleteCheckoutAddress,
  fetchAddresses,
  receiveNewShippingField,
  setSelectedSuggestedAddress,
  storeSelectedAddress
} from 'actions/checkout/addresses';
import {
  addNewPayment,
  clearSelectedPayment,
  defaultSelectedPayment,
  fetchPayments,
  goToPaymentStep,
  newPaymentPrimary,
  receiveNewPaymentField,
  setPaymentsAreLoadingMessage,
  updatePaymentAddress,
  verifyCreditCard
} from 'actions/checkout/payments';
import {
  advanceCheckout,
  fetchCheckoutData,
  setConfirmCardMessage,
  setUseNameOnAccount,
  setUsePromoBalance,
  storeNoPromoCodeWarning,
  updateCheckoutForTargetStep
} from 'actions/checkout/purchase';
import {
  showGiftCardModal,
  updateGiftCardCode,
  verifyGiftCard
} from 'actions/checkout/giftCards';
import AddressList from 'components/checkout/caap/address/addresslist/AddressList';
import BillingAddress from 'components/checkout/caap/address/BillingAddress';
import ContentBox from 'components/checkout/caap/ContentBox';
import GiftCardContainer from 'components/checkout/caap/GiftCardContainer';
import NewBillingAddressModal from 'components/checkout/caap/address/NewBillingAddressModal';
import NewPaymentTypeModal from 'components/checkout/caap/payment/NewPaymentTypeModal';
import Payment from 'components/checkout/caap/payment/Payment';
import PaymentList from 'components/checkout/caap/payment/paymentlist/PaymentList';
import SectionLoading from 'components/checkout/caap/SectionLoading';
import SmallScreenOnly from 'components/checkout/caap/SmallScreenOnly';
import StepSection from 'components/checkout/caap/StepSection';
import SuggestedAddresses from 'components/checkout/caap/address/SuggestedAddresses';
import { getPathFromStep } from 'helpers/CheckoutFlowControl';
import {
  inIframe,
  isChangingPayment,
  isChangingShipping,
  needsToReAssociatePaymentToAddressForPayment
} from 'helpers/CheckoutUtils';
import { isCreditCardExpired } from 'helpers/MyAccountUtils';
import styles from 'styles/containers/checkout/paySection.scss';
import marketplace from 'cfg/marketplace.json';
import { isDesktop } from 'helpers/ClientUtils';

export class PaySection extends Component {
  componentDidMount() {
    const {
      location: { search },
      checkoutData: {
        addresses: { new: newAddress },
        paymentTypes: { new: newPayment, saved: savedPayments },
        purchaseData: {
          purchaseCreditCard,
          targetStep
        }
      },
      clearSelectedPayment,
      goToPaymentStep,
      storeSelectedAddress
    } = this.props;

    const { editDestinationId } = queryString.parse(search);
    const isPaymentStep = targetStep === PAYMENT_STEP;
    const isBillingStep = targetStep === EDIT_BILLING_ADDRESS_STEP || targetStep === NEW_BILLING_ADDRESS_STEP;
    const isNewPaymentStep = targetStep === NEW_PAYMENT_STEP;
    const hasSavedPayments = savedPayments && savedPayments.length;
    const hasPaymentInstrumentId = newPayment.paymentInstrumentId && newPayment.paymentInstrumentId.length;
    const hasAddressId = newAddress.addressId && newAddress.addressId.length;
    const isSelectBillingStep = targetStep === SELECT_BILLING_ADDRESS_STEP;

    if (isPaymentStep) {
      if (hasSavedPayments) {
        // could default selected here if we wanted...
        // this.props.defaultSelectedPayment();
      } else if (hasPaymentInstrumentId) {
        clearSelectedPayment();
      }
    } else if (isNewPaymentStep && hasPaymentInstrumentId) {
      clearSelectedPayment();
    } else if (isBillingStep && !hasAddressId && editDestinationId) {
      storeSelectedAddress(editDestinationId);
    }

    // If user hard refreshes the page when selecting a billing address, need to
    // kick them back to the select payment screen if there is no purchase card,
    // as you cant select a billing address if there is no card on the purchase.
    // How this can occur:
    //  - have GC on purchase that covers cost of purchase
    //  - change from using the GC to a CC
    //  - go to change billing address
    //  - refresh the page
    // When page refreshes, it'll auto use the GC on the purchase
    if (isSelectBillingStep && !purchaseCreditCard) {
      goToPaymentStep();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      clearSelectedAddress,
      clearAddressErrors,
      clearSelectedPayment,
      checkoutData: { purchaseData: { targetStep } }
    } = this.props;
    const { checkoutData: { purchaseData: { targetStep: prevStep } } } = prevProps;
    const isNewPaymentStep = targetStep === NEW_PAYMENT_STEP;
    const isNewBillingAddressStep = targetStep === NEW_BILLING_ADDRESS_STEP;
    const hasChangedStep = targetStep !== prevStep;

    // when user jumps to the 'add new payment' screen, clear existing payment
    if (hasChangedStep && isNewPaymentStep) {
      clearSelectedPayment();
    }

    // when user jumps to the 'add new billing' screen, clear any existing address
    if (hasChangedStep && isNewBillingAddressStep) {
      clearSelectedAddress();
      clearAddressErrors();
    }
  }

  lookupAddressById = addressId => {
    const {
      checkoutData: {
        addresses: { saved: savedAddresses }
      }
    } = this.props;

    if (!savedAddresses.length) {
      return {};
    }

    const addressInfo = savedAddresses.find(item => item.addressId === addressId);

    if (!addressInfo) {
      return {};
    }

    const {
      mailingAddress: {
        addressLine1,
        addressLine2,
        postalCode,
        city,
        stateOrRegion,
        countryCode
      },
      name: {
        fullName
      },
      phone: {
        voice: {
          primary: { number }
        }
      }
    } = addressInfo;

    return {
      addressId: addressId || '',
      addressLine1: addressLine1 || '',
      addressLine2: addressLine2 || '',
      city: city || '',
      countryCode: countryCode || '',
      fullName: fullName || '',
      postalCode: postalCode || '',
      primaryVoiceNumber: number || '',
      stateOrRegion: stateOrRegion || ''
    };
  }

  showPaymentStep = () => {
    const { purchaseData: { targetStep }, shipOptions } = this.props.checkoutData;
    return targetStep === PAYMENT_STEP
    || targetStep === NEW_PAYMENT_STEP
    || targetStep === SELECT_BILLING_ADDRESS_STEP
    || targetStep === NEW_BILLING_ADDRESS_STEP
    || (targetStep === REVIEW_STEP && !shipOptions.showModal);
  }

  onShipAddressSelected = addressId => {
    const { receiveNewShippingField } = this.props;
    trackEvent('TE_CHECKOUT_BL_SELECT_ADDRESS');
    receiveNewShippingField('addressId', addressId);
  }

  onShippingAddressFieldChange = e => {
    const { receiveNewShippingField } = this.props;
    const { name, value } = e.currentTarget;
    receiveNewShippingField(name, value);
  }

  onPaymentTypeFieldChange = e => {
    const { name, value } = e.currentTarget;
    this.props.receiveNewPaymentField(name, value);
  }

  onAddNewPaymentClick = e => {
    e.preventDefault();
    trackEvent('TE_CHECKOUT_PM_ADD_NEW_PAYMENT');
    const {
      addNewPayment,
      receiveNewPaymentField,
      checkoutData: { paymentTypes: { new: { cc, paymentInstrumentId } }, purchaseData: { isFetchingCheckoutData } }
    } = this.props;

    if (!isFetchingCheckoutData) {
      if (cc && cc.length && paymentInstrumentId === 'savedBalance') {
        receiveNewPaymentField('paymentInstrumentId', '');
      }

      addNewPayment();
    }
  }

  onCloseAddressModalOverlayClick = e => {
    e.preventDefault();
    const { updateCheckoutForTargetStep } = this.props;
    trackEvent('TE_CHECKOUT_SPC_CLOSE_SEL_ADDRESS_MODAL');
    updateCheckoutForTargetStep({ targetStep: REVIEW_STEP, newUrl: getPathFromStep(REVIEW_STEP) });
  }

  onCloseBillingAddressModalOverlayClick = e => {
    e.preventDefault();
    const { updateCheckoutForTargetStep } = this.props;
    trackEvent('TE_CHECKOUT_SPC_CLOSE_ADDRESS_MODAL', 'billing');
    updateCheckoutForTargetStep({ targetStep: REVIEW_STEP, newUrl: getPathFromStep(REVIEW_STEP) });
  }

  onCloseNewPaymentModalOverlayClick = e => {
    e.preventDefault();
    const { updateCheckoutForTargetStep } = this.props;
    trackEvent('TE_CHECKOUT_SPC_CLOSE_PAYMENT_MODAL');
    updateCheckoutForTargetStep({ targetStep: PAYMENT_STEP, newUrl: getPathFromStep(PAYMENT_STEP) });
  }

  onCloseSuggestedAddressModalOverlayClick = e => {
    e.preventDefault();
    trackEvent('TE_CHECKOUT_SPC_CLOSE_SUGG_ADDRESS_MODAL');
    this.onUseThisSuggestedAddressClick(e);
  }

  canUseThisPayment = () => {
    const { checkoutData: { purchaseData: { doesPurchaseRequireCC }, paymentTypes } } = this.props;
    const hasSelectedPayment = paymentTypes.new && !!paymentTypes.new.paymentInstrumentId;
    const notExpiredPayment = !isCreditCardExpired(paymentTypes.new);

    return this.hasNonExpiredPurchaseCC()
      || !doesPurchaseRequireCC
      || (hasSelectedPayment && notExpiredPayment);
  }

  hasNonExpiredPurchaseCC = () => {
    const { checkoutData: { purchaseData: { purchaseCreditCard } } } = this.props;
    return purchaseCreditCard && !isCreditCardExpired(purchaseCreditCard);
  }

  onPaymentSelected = (paymentInstrumentId, isExpired) => {
    const {
      checkoutData,
      receiveNewPaymentField,
      setConfirmCardMessage,
      setUsePromoBalance
    } = this.props;

    const { purchaseData: { doesPurchaseRequireCC }, paymentTypes } = checkoutData;
    const { saved: savedPayments } = paymentTypes;

    const selectedPayment = (savedPayments || []).find(
      item => item.paymentInstrumentId === paymentInstrumentId
    ) || {};

    setConfirmCardMessage(null);
    trackEvent('TE_CHECKOUT_PL_SELECT_PAYMENT');
    receiveNewPaymentField('isExpired', isExpired);
    receiveNewPaymentField('paymentInstrumentId', paymentInstrumentId);
    receiveNewPaymentField('expiration', `${selectedPayment.expirationMonth} ${selectedPayment.expirationYear}`);
    receiveNewPaymentField('expirationMonth', selectedPayment.expirationMonth);
    receiveNewPaymentField('expirationYear', selectedPayment.expirationYear);
    receiveNewPaymentField('name', selectedPayment.fullName);
    receiveNewPaymentField('errors', {});

    if (!doesPurchaseRequireCC) {
      setUsePromoBalance(paymentInstrumentId === 'savedBalance');
    }
  }

  onCountryDropdownChange = e => {
    const { receiveNewShippingField } = this.props;
    const { name, value } = e.currentTarget;
    receiveNewShippingField(name, value);
  }

  onToggleGiftCardModalClick = () => {
    const { showGiftCardModal, checkoutData: { giftCards: { showModal } } } = this.props;
    const newState = !showModal;
    trackEvent('TE_CHECKOUT_GC_TOGGLE', `${newState}`);
    showGiftCardModal(newState);
  }

  onCardValidate = (e, paymentIdToUse) => {
    e.preventDefault();
    trackEvent('TE_CHECKOUT_PL_VERIFY_CC');
    const {
      checkoutData: {
        purchaseData: {
          data,
          isFetchingCheckoutData
        }
      },
      setConfirmCardMessage,
      verifyCreditCard
    } = this.props;

    const input = document.getElementById(`confirmCard-${paymentIdToUse}`);
    const ccValue = input.value;

    if (!ccValue) {
      setConfirmCardMessage('Please enter card number.');
    }

    if (ccValue && !isFetchingCheckoutData) {
      verifyCreditCard({ number: ccValue, paymentInstrumentId: paymentIdToUse, addressId: data.shippingAddressId });
    }
  }

  onChangeBillingAddressClick = () => {
    trackEvent('TE_CHECKOUT_SPC_CHANGE_BILLING');
    const {
      fetchAddresses,
      receiveNewShippingField
    } = this.props;

    fetchAddresses();
    receiveNewShippingField('isBilling', true);
  }

  onChangePaymentClick = () => {
    const {
      fetchPayments
    } = this.props;

    trackEvent('TE_CHECKOUT_SPC_CHANGE_PAYMENT');
    fetchPayments();
  }

  onCloseLinkClick = e => {
    e.preventDefault();
    const { defaultSelectedPayment, updateCheckoutForTargetStep } = this.props;
    trackEvent('TE_CHECKOUT_SPC_CLOSE_PAYMENT');
    defaultSelectedPayment();
    updateCheckoutForTargetStep({ targetStep: REVIEW_STEP, newUrl: getPathFromStep(REVIEW_STEP) });
  }

  onGiftCardChange = e => {
    const { updateGiftCardCode } = this.props;
    const { value } = e.target;
    updateGiftCardCode(value);
  }

  onGiftCardVerify = e => {
    e.preventDefault();
    trackEvent('TE_CHECKOUT_GC_APPLY_PROMO');

    const {
      checkoutData: { giftCards: { code }, isFetchingCheckoutData },
      storeNoPromoCodeWarning,
      verifyGiftCard
    } = this.props;

    if (!code) {
      storeNoPromoCodeWarning();
    }

    if (code && !isFetchingCheckoutData) {
      verifyGiftCard(code);
    }
  }

  onMakeNewPaymentPrimaryClick = () => {
    const { checkoutData: { paymentTypes }, newPaymentPrimary } = this.props;
    const newState = !paymentTypes.isNewPrimary;
    trackEvent('TE_CHECKOUT_PM_TOGGLE_PRIMARY', `${newState}`);
    newPaymentPrimary(newState);
  }

  onSuggestedAddressSelected = addressId => {
    const { setSelectedSuggestedAddress } = this.props;
    trackEvent('TE_CHECKOUT_SA_SELECT_ADDRESS');
    setSelectedSuggestedAddress(addressId);
  }

  onToggleUseNameOnAccountClick = () => {
    const {
      checkoutData: {
        purchaseData: {
          useNameOnAccount, data: { nameOnAccount }
        }
      },
      receiveNewPaymentField,
      setUseNameOnAccount
    } = this.props;

    if (useNameOnAccount) {
      receiveNewPaymentField('name', '');
      trackEvent('TE_CHECKOUT_PM_TOGGLE_NAME', 'false');
      setUseNameOnAccount(false);
    } else {
      receiveNewPaymentField('name', nameOnAccount);
      trackEvent('TE_CHECKOUT_PM_TOGGLE_NAME', 'true');
      setUseNameOnAccount(true);
    }
  }

  onUpdatePaymentAddressClick = selectedBillingAddressId => {
    const {
      advanceCheckout,
      updatePaymentAddress,
      checkoutData
    } = this.props;

    const {
      purchaseData: {
        isFetchingCheckoutData,
        purchaseBillingAddressId
      }
    } = checkoutData;

    trackEvent('TE_CHECKOUT_BL_USE_ADDRESS');

    if (!isFetchingCheckoutData) {
      if (purchaseBillingAddressId === selectedBillingAddressId) {
        advanceCheckout();
      } else {
        updatePaymentAddress({ billingAddressId: selectedBillingAddressId });
      }
    }
  }

  onUseBillingAddressClick = e => {
    e.preventDefault();
    const { addNewAddress, checkoutData: { purchaseData: { isFetchingCheckoutData } } } = this.props;
    trackEvent('TE_CHECKOUT_AM_BILL_TO_ADDRESS');
    if (!isFetchingCheckoutData) {
      addNewAddress();
    }
  }

  onUsePromoBalanceClick = () => {
    const {
      checkoutData: { purchaseData: { usePromoBalance } },
      setUsePromoBalance
    } = this.props;
    const newState = !usePromoBalance;
    trackEvent('TE_CHECKOUT_PL_TOGGLE_USE_PROMO', `${newState}`);
    setUsePromoBalance(newState);
  }

  onUseThisPaymentClick = () => {
    const {
      checkoutData,
      fetchCheckoutData,
      setPaymentsAreLoadingMessage
    } = this.props;

    const {
      purchaseData: {
        doesPurchaseRequireCC,
        isFetchingCheckoutData,
        purchaseCreditCard,
        shippingAddressId
      },
      paymentTypes: { new: newPayment, saved: savedPayments }
    } = checkoutData;

    const selectedPaymentId = (newPayment && newPayment.paymentInstrumentId)
      || (!doesPurchaseRequireCC && 'savedBalance')
      || (purchaseCreditCard && purchaseCreditCard.paymentInstrumentId)
      || '';

    if (!isFetchingCheckoutData) {
      setPaymentsAreLoadingMessage('Setting your payment method...');
      if (selectedPaymentId === 'savedBalance') {
        trackEvent('TE_CHECKOUT_PL_USE_PAYMENT', 'savedBalance');
        fetchCheckoutData({
          advanceOnSuccess: true,
          paymentMethods: [],
          showFullPageSpinner: false,
          usePromoBalance: true
        });
      } else {
        // track which was clicked from positional point of view
        const index = savedPayments.findIndex(item => item.paymentInstrumentId === selectedPaymentId);

        trackEvent('TE_CHECKOUT_PL_USE_PAYMENT', `cc:${index}`);
        const paymentMethods = [{
          paymentInstrumentId: selectedPaymentId,
          paymentMethodCode: 'CC'
        }];

        if (doesPurchaseRequireCC) {
          fetchCheckoutData({ paymentMethods, showFullPageSpinner: false, advanceOnSuccess: true, addressId: shippingAddressId });
        } else {
          fetchCheckoutData({ usePromoBalance: false, paymentMethods, showFullPageSpinner: false, advanceOnSuccess: true });
        }

      }
    }
  }

  onUpdateCreditCardClick = e => {
    const { addNewPayment, checkoutData } = this.props;
    const {
      purchaseData: {
        isFetchingCheckoutData
      }
    } = checkoutData;

    e.preventDefault();

    trackEvent('TE_CHECKOUT_PL_UPDATE_EXP');

    if (!isFetchingCheckoutData) {
      addNewPayment();
    }
  }

  onUseThisSuggestedAddressClick = e => {
    e.preventDefault();
    const {
      addNewAddress,
      advanceCheckout,
      checkoutData: {
        purchaseData: {
          data: { shippingAddress },
          purchaseCreditCard
        },
        addresses: {
          suggestedAddresses: { addresses, isBilling, selectedAddressId }
        }
      },
      clearSuggestedAddresses,
      receiveNewShippingField
    } = this.props;

    const suggestedAddressMatch = selectedAddressId.match(/(.*)-(\d+)$/);

    let { fullName, phoneNumber } = shippingAddress;

    if (isBilling) {
      ({ fullName } = purchaseCreditCard.billingAddress);
      ({ phoneNumber } = purchaseCreditCard.billingAddress);
    }

    if (suggestedAddressMatch) {
      const {
        mailingAddress: {
          addressLine1,
          addressLine2,
          city,
          countryCode,
          postalCode,
          stateOrRegion
        }
      } = addresses[suggestedAddressMatch[2]];

      trackEvent('TE_CHECKOUT_SA_USE_ADDRESS', 'suggested');

      receiveNewShippingField('addressId', suggestedAddressMatch[1]);
      receiveNewShippingField('fullName', fullName);
      receiveNewShippingField('primaryVoiceNumber', phoneNumber);
      receiveNewShippingField('addressLine1', addressLine1);
      receiveNewShippingField('addressLine2', addressLine2 ? addressLine2 : '');
      receiveNewShippingField('city', city);
      receiveNewShippingField('countryCode', countryCode);
      receiveNewShippingField('stateOrRegion', stateOrRegion);
      receiveNewShippingField('postalCode', postalCode);
      addNewAddress();
    } else {
      trackEvent('TE_CHECKOUT_SA_USE_ADDRESS', 'original');
      clearSuggestedAddresses();
      advanceCheckout();
    }
  }

  render() {
    const { checkoutData } = this.props;
    const { testId } = this.context;

    const {
      addresses,
      giftCards,
      paymentTypes,
      purchaseData: {
        confirmCardMessage,
        targetStep,
        cartType,
        constraintViolations,
        data,
        doesPurchaseRequireCC,
        isFetchingCheckoutData,
        pageHistory,
        purchaseBillingAddressId,
        purchaseCreditCard,
        useNameOnAccount,
        usePromoBalance
      }
    } = checkoutData;

    const {
      new: newAddress,
      saved: savedAddresses,
      suggestedAddresses = {}
    } = addresses;

    const {
      eligibleBalances,
      purchaseId,
      shippingAddressId
    } = data;

    const {
      loadingMessage,
      new: newPayment,
      saved: savedPayments
    } = paymentTypes;

    const {
      showSuggestedModal
    } = suggestedAddresses;

    let showNewAddress = false, showNewPayment = false, showSavedAddresses = false, showSavedPayments = false;
    if (targetStep === PAYMENT_STEP) {
      if (savedPayments && savedPayments.length) {
        showSavedPayments = true;
      } else {
        showNewPayment = true;
      }
    } else if (targetStep === NEW_PAYMENT_STEP) {
      showNewPayment = true;
    } else if (targetStep === SELECT_BILLING_ADDRESS_STEP) {
      showSavedAddresses = true;
    } else if (targetStep === NEW_BILLING_ADDRESS_STEP) {
      showNewAddress = true;
    } else if (targetStep === EDIT_BILLING_ADDRESS_STEP) {
      showNewAddress = true;
    }

    const showCCIVRPayment = inIframe();
    const isBillingSameAsShipping = purchaseBillingAddressId === shippingAddressId;
    const purchaseBillingAddress = purchaseCreditCard && purchaseCreditCard.billingAddress || {};
    const selectedBillingAddressId = (newAddress && newAddress.addressId) || purchaseBillingAddressId || savedAddresses && savedAddresses.length && savedAddresses[0].addressId;
    const selectedPaymentId = (newPayment && newPayment.paymentInstrumentId) || (!doesPurchaseRequireCC && 'savedBalance') || (purchaseCreditCard && purchaseCreditCard.paymentInstrumentId) || '';
    const showPaymentStep = this.showPaymentStep();
    const showNewPaymentAsModal = savedPayments && savedPayments.length;
    const selectedPayment = (paymentTypes.saved || []).find(
      item => item.paymentInstrumentId === selectedPaymentId);
    const hasToValidatePayment = selectedPayment && selectedPayment.paymentInstrumentId && needsToReAssociatePaymentToAddressForPayment(constraintViolations, selectedPayment.paymentInstrumentId);
    const selectedPaymentNeedsConfirmation = hasToValidatePayment || (selectedPayment && selectedPayment.associatedAddress && selectedPayment.associatedAddress.associated === false) || (selectedPayment && !selectedPayment.associatedAddress);
    const isChangeShipping = isChangingShipping(targetStep);
    const isChangePayment = isChangingPayment(targetStep);
    const showSuggestedAddresses = showSuggestedModal && isChangePayment;
    const canUsePayment = this.canUseThisPayment() && !selectedPaymentNeedsConfirmation;

    const paymentDetailStyles = cn(
      {
        [styles.hideSmallScreen]: (showSavedAddresses || showNewAddress) || (showNewPaymentAsModal && showNewPayment),
        [styles.hidden]: showSavedPayments || (!showNewPaymentAsModal && showNewPayment)
      }
    );

    const billingAddressContent = <BillingAddress
      address={purchaseCreditCard && purchaseCreditCard.billingAddress}
      billingModalOpen={showNewAddress || showSavedAddresses}
      isBillingSameAsShipping={isBillingSameAsShipping}
      onChangeBillingAddressClick={this.onChangeBillingAddressClick}
      purchaseId={purchaseId}
      showBillingAddress={doesPurchaseRequireCC || !usePromoBalance} />;

    const giftCardContainerContent = <GiftCardContainer
      code={giftCards.code || ''}
      constraintViolations={constraintViolations}
      isFetchingCheckoutData={isFetchingCheckoutData}
      onGiftCardChange={this.onGiftCardChange}
      onGiftCardVerify={this.onGiftCardVerify}
      onToggleGiftCardModalClick={this.onToggleGiftCardModalClick}
      showGiftCardModal={giftCards.showModal}
      withOffset={true} />;

    if (showSuggestedAddresses) {
      let originalAddress = purchaseBillingAddress;

      if (suggestedAddresses.originalAddressId !== purchaseBillingAddress.addressId) {
        originalAddress = this.lookupAddressById(suggestedAddresses.originalAddressId);
      }
      return (
        <StepSection addOverlay={isFetchingCheckoutData} isCurrentStep={showPaymentStep} dataTestIdLoader={`${testId('spinner')}`}>
          <SuggestedAddresses
            address={originalAddress}
            isBilling={true}
            isFetchingCheckoutData={isFetchingCheckoutData}
            onCloseModalOverlayClick={this.onCloseSuggestedAddressModalOverlayClick}
            onSuggestedAddressSelected={this.onSuggestedAddressSelected}
            onUseThisAddressClick={this.onUseThisSuggestedAddressClick}
            pageHistory={pageHistory}
            selectedAddressId={suggestedAddresses.selectedAddressId}
            suggestedAddresses={suggestedAddresses.addresses} />
        </StepSection>
      );
    }

    if (isDesktop(marketplace) && loadingMessage && loadingMessage.length) {
      return (
        <StepSection
          showTitleBar={false}
          title="Select payment"
          stepNumber="1"
          addOverlay={false}
          isCurrentStep={true}
          dataTestIdLoader={`${testId('spinner')}`}>
          <div>
            <SmallScreenOnly><h2>Payment method</h2></SmallScreenOnly>
            <ContentBox isSolo={true} isNonRadio={true}>
              <SectionLoading
                heading="Payment method"
                msg={loadingMessage}
                step="2" />
            </ContentBox>
          </div>
        </StepSection>
      );
    }

    return (
      <StepSection
        addOverlay={false}
        linkText="Close"
        linkTo={`${CHECKOUT_STEP_MAP[REVIEW_STEP]}?referrer=spc&pid=${purchaseId}`}
        hideContent={isChangeShipping && (doesPurchaseRequireCC && !purchaseCreditCard)}
        showTitleBar={isChangePayment || (doesPurchaseRequireCC && !purchaseCreditCard)}
        isCurrentStep={showPaymentStep}
        onLinkClick={this.onCloseLinkClick}
        showLink={isChangePayment && (!doesPurchaseRequireCC || purchaseCreditCard)}
        stepNumber="2"
        title="Select a payment"
        dataTestIdLoader={`${testId('spinner')}`}>
        {
          !isChangePayment && <>
            <div className={paymentDetailStyles}>
              <SmallScreenOnly><h2>Payment Information</h2></SmallScreenOnly>
              <ContentBox isFirst={true} isNonRadio={true}>
                <Payment
                  cartType={cartType}
                  doesPurchaseRequireCC={doesPurchaseRequireCC}
                  eligibleBalances={eligibleBalances}
                  paymentMethod={purchaseCreditCard}
                  purchaseId={purchaseId}
                  onChangePaymentClick={this.onChangePaymentClick}
                  usePromoBalance={usePromoBalance} />
              </ContentBox>
              {
                doesPurchaseRequireCC || !usePromoBalance
                  ? <>
                    <div className={styles.smallScreenOnly}>
                      <ContentBox isNonRadio={true}>
                        { billingAddressContent }
                      </ContentBox>
                    </div>
                    <div className={`${styles.hideSmallScreen} ${styles.billingAddressWrapper}`}>
                      { billingAddressContent }
                    </div>
                  </>
                  : null
              }

              <>
                <div className={styles.smallScreenOnly}>
                  <ContentBox isNonRadio={true} isLast={true}>
                    { giftCardContainerContent }
                  </ContentBox>
                </div>
                <div className={`${styles.hideSmallScreen} ${styles.gcContainerWrapper}`}>
                  { giftCardContainerContent }
                </div>
              </>
            </div>
          </>
        }

        {
          !hasToValidatePayment && showSavedAddresses
            ? <AddressList
              addresses={savedAddresses}
              purchaseBillingAddressId={purchaseBillingAddressId}
              isBilling={true}
              isFetchingCheckoutData={isFetchingCheckoutData}
              onAddressSelected={this.onShipAddressSelected}
              onCloseModalOverlayClick={this.onCloseAddressModalOverlayClick}
              onEditAddressClick={f => f}
              onUseThisAddressClick={this.onUpdatePaymentAddressClick}
              pageHistory={pageHistory}
              purchaseId={purchaseId}
              selectedAddressId={selectedBillingAddressId}
              showAsModal={true} />
            : null
        }

        {
          showNewAddress
            ? <NewBillingAddressModal
              address={newAddress}
              isFetchingCheckoutData={isFetchingCheckoutData}
              onAddressFieldChange={this.onShippingAddressFieldChange}
              onCloseModalOverlayClick={this.onCloseBillingAddressModalOverlayClick}
              onCountryDropdownChange={this.onCountryDropdownChange}
              onUseBillingAddressClick={this.onUseBillingAddressClick}
              pageHistory={pageHistory}
              showAsModal={showNewPaymentAsModal} />
            : null
        }

        {
          showSavedPayments
            ? <PaymentList
              canUseThisPayment={canUsePayment}
              cartType={cartType}
              confirmCardMessage={confirmCardMessage}
              constraintViolations={constraintViolations}
              showCCIVRPayment={showCCIVRPayment}
              eligibleBalances={eligibleBalances}
              giftCards={giftCards}
              isFetchingCheckoutData={isFetchingCheckoutData}
              newPayment={newPayment}
              onCardValidate={this.onCardValidate}
              onEditPaymentClick={this.onEditPaymentClick}
              onGiftCardChange={this.onGiftCardChange}
              onGiftCardVerify={this.onGiftCardVerify}
              onPaymentSelected={this.onPaymentSelected}
              onPaymentTypeFieldChange={this.onPaymentTypeFieldChange}
              onToggleGiftCardModalClick={this.onToggleGiftCardModalClick}
              onUpdateCreditCardClick={this.onUpdateCreditCardClick}
              onUsePromoBalanceClick={this.onUsePromoBalanceClick}
              onUseThisPaymentClick={this.onUseThisPaymentClick}
              pageHistory={pageHistory}
              payment={newPayment}
              purchaseId={purchaseId}
              savedPayments={savedPayments}
              selectedPaymentId={selectedPaymentId}
              selectedPaymentNeedsConfirmation={selectedPaymentNeedsConfirmation}
              showAsRadio={!doesPurchaseRequireCC}
              usePromoBalance={usePromoBalance}
            />
            : null
        }

        {
          showNewPayment
            ? <>
            <NewPaymentTypeModal
              constraintViolations={constraintViolations}
              giftCards={giftCards}
              hasSavedPayments={!!(savedPayments && savedPayments.length)}
              isFetchingCheckoutData={isFetchingCheckoutData}
              isNewPrimary={paymentTypes.isNewPrimary}
              onAddNewPaymentClick={this.onAddNewPaymentClick}
              onCloseModalOverlayClick={this.onCloseNewPaymentModalOverlayClick}
              onGiftCardChange={this.onGiftCardChange}
              onGiftCardVerify={this.onGiftCardVerify}
              onMakeNewPaymentPrimaryClick={this.onMakeNewPaymentPrimaryClick}
              onPaymentTypeFieldChange={this.onPaymentTypeFieldChange}
              onToggleGiftCardModalClick={this.onToggleGiftCardModalClick}
              onToggleUseNameOnAccountClick={this.onToggleUseNameOnAccountClick}
              pageHistory={pageHistory}
              payment={newPayment}
              showAsModal={showNewPaymentAsModal}
              useNameOnAccount={useNameOnAccount}
              showCCIVRPayment={showCCIVRPayment}
              onCloseCCIVR={this.onCloseLinkClick}
            />
            { !savedPayments?.length && <div className={styles.gcWrapperNoPayments}>{giftCardContainerContent}</div> }
            </>
            : null
        }
      </StepSection>
    );
  }
}

function mapStateToProps(state) {
  const { cart, checkoutData, routing: { locationBeforeTransitions } } = state;

  return {
    checkoutData,
    cart,
    location: locationBeforeTransitions
  };
}

PaySection.contextTypes = {
  testId: PropTypes.func
};

export default connect(mapStateToProps, {
  addNewAddress,
  addNewPayment,
  advanceCheckout,
  clearAddressErrors,
  clearSelectedAddress,
  clearSuggestedAddresses,
  clearSelectedPayment,
  deleteCheckoutAddress,
  defaultSelectedPayment,
  fetchAddresses,
  fetchPayments,
  fetchCheckoutData,
  goToPaymentStep,
  newPaymentPrimary,
  receiveNewPaymentField,
  receiveNewShippingField,
  setConfirmCardMessage,
  setPaymentsAreLoadingMessage,
  setSelectedSuggestedAddress,
  setUseNameOnAccount,
  setUsePromoBalance,
  showGiftCardModal,
  updateCheckoutForTargetStep,
  updateGiftCardCode,
  updatePaymentAddress,
  verifyCreditCard,
  verifyGiftCard,
  storeSelectedAddress,
  storeNoPromoCodeWarning
})(PaySection);
